import { z } from "zod";

export const Sizes: Record<string, [w: number, h: number]> = {
  "6x4": [6, 4],
  "8x4": [8, 4],
  "8x6": [8, 6],
  "12x4": [12, 4],
  "12x6": [12, 6],
  "12x8": [12, 8],
};

// Common options shared across all chart types
const commonOptionsSchema = z.object({
  plugins: z
    .object({
      legend: z
        .object({
          display: z.boolean().default(true),
          position: z.enum(["top", "bottom", "left", "right"]).default("top"),
        })
        .optional(),
    })
    .optional(),
});

// Type-specific options
const barOptionsSchema = commonOptionsSchema.extend({
  scales: z
    .object({
      x: z
        .object({
          stacked: z.boolean().optional(),
          beginAtZero: z.boolean().optional(),
        })
        .optional(),
      y: z
        .object({
          stacked: z.boolean().optional(),
          beginAtZero: z.boolean().optional(),
          stepSize: z.number().optional(),
          max: z.number().optional(),
          min: z.number().optional(),
        })
        .optional(),
    })
    .optional(),
});

const lineOptionsSchema = commonOptionsSchema.extend({
  scales: z
    .object({
      x: z
        .object({
          type: z.enum(["linear", "logarithmic", "category"]).optional(),
        })
        .optional(),
      y: z
        .object({
          type: z.enum(["linear", "logarithmic", "category"]).optional(),
          beginAtZero: z.boolean().optional(),
          stepSize: z.number().optional(),
          max: z.number().optional(),
          min: z.number().optional(),
        })
        .optional(),
    })
    .optional(),
  elements: z
    .object({
      line: z
        .object({
          tension: z.number().min(0).max(1).optional(),
        })
        .optional(),
    })
    .optional(),
});

const pieOptionsSchema = commonOptionsSchema.extend({
  cutout: z.string().optional(),
  radius: z.string().optional(),
});

const doughnutOptionsSchema = pieOptionsSchema.extend({
  cutout: z.string().default("50%"),
});

const polarAreaOptionsSchema = commonOptionsSchema.extend({
  startAngle: z.number().optional(),
});

const radarOptionsSchema = commonOptionsSchema.extend({
  scales: z
    .object({
      r: z
        .object({
          beginAtZero: z.boolean().optional(),
          min: z.number().optional(),
          max: z.number().optional(),
        })
        .optional(),
    })
    .optional(),
});

const scatterOptionsSchema = commonOptionsSchema.extend({
  scales: z
    .object({
      x: z
        .object({
          type: z.enum(["linear", "logarithmic", "time"]).optional(),
        })
        .optional(),
      y: z
        .object({
          type: z.enum(["linear", "logarithmic", "time"]).optional(),
        })
        .optional(),
    })
    .optional(),
});

const bubbleOptionsSchema = scatterOptionsSchema.extend({
  scales: z
    .object({
      x: z
        .object({
          type: z.literal("linear"),
        })
        .optional(),
      y: z
        .object({
          type: z.literal("linear"),
        })
        .optional(),
    })
    .optional(),
});

const baseChartSchema = z.object({
  labelsArrayField: z.null(),
  labelsDeepFieldKey: z.null(),
  labelsTransformation: z.null(),
  datasets: z.null(),
  datasetArrayField: z.null(),
  titleFieldKey: z.null(),
  titleTransformation: z.null(),
  dataAndLabelArrayFieldKey: z.null(),
  timeSeriesFieldKey: z.null(),
  labelTransformation: z.null(),
  title: z.string().default("Custom Chart"),
  type: z.enum([
    "bar",
    "line",
    "scatter",
    "bubble",
    "pie",
    "doughnut",
    "polarArea",
    "radar",
  ]),
  options: z.discriminatedUnion("type", [
    z.object({ type: z.literal("bar"), ...barOptionsSchema.shape }),
    z.object({ type: z.literal("line"), ...lineOptionsSchema.shape }),
    z.object({ type: z.literal("scatter"), ...scatterOptionsSchema.shape }),
    z.object({ type: z.literal("bubble"), ...bubbleOptionsSchema.shape }),
    z.object({ type: z.literal("pie"), ...pieOptionsSchema.shape }),
    z.object({ type: z.literal("doughnut"), ...doughnutOptionsSchema.shape }),
    z.object({ type: z.literal("polarArea"), ...polarAreaOptionsSchema.shape }),
    z.object({ type: z.literal("radar"), ...radarOptionsSchema.shape }),
  ]),
});

const generalDatasetSchema = z.object({
  parsing: z
    .object({
      xAxisKey: z.string().optional(),
      yAxisKey: z.string().optional(),
    })
    .optional(),
  indexAxis: z.enum(["x", "y"]),
  label: z.string(),
  dataArrayField: z.string(),
  dataFieldKey: z.string(),
  dataGrouping: z
    .enum([
      "none",
      "sum",
      "average",
      "median",
      "min",
      "max",
      "count",
      "stdDev",
      "variance",
      "range",
      "mode",
      "first",
      "last",
      "uniqueCount",
      "custom",
    ])
    .default("none"),
  dataGroupingFormula: z.string().optional(),
  backgroundColor: z.string().optional(),
  borderColor: z.string().optional(),
  color: z.string().optional(),
});

export const chartSettingsFormSchema = z.discriminatedUnion("responseShape", [
  baseChartSchema.extend({
    responseShape: z.literal("general"),
    labelsArrayField: z.string(),
    labelsDeepFieldKey: z.string(),
    labelsTransformation: z.string().default("none"),
    datasets: z.array(generalDatasetSchema),
  }),
  baseChartSchema.extend({
    responseShape: z.literal("arrayOfDatasets"),
    datasetArrayField: z.string(),
    titleFieldKey: z.string(),
    titleTransformation: z.string().default("none"),
    dataAndLabelArrayFieldKey: z.string(),
  }),
  baseChartSchema.extend({
    responseShape: z.literal("timeSeries"),
    timeSeriesFieldKey: z.string(),
    labelTransformation: z.string().default("none"),
  }),
]);
